import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import Axios from "axios";
import MaterialTable from "material-table";
import moment from "moment-timezone";

import { calculateTargetProd } from "../../../../util/targetCalc";
import { months } from "../../../../util/month.js";
import { toTitleCase, useFormatter } from "../../../../util/formatter";
import GYAxios from "../../../../util/GYAxios";
import { useSite } from "../../../../util/sites";

import DefaultSelect from "../../../general/DefaultSelect";
import styles from './TargetViewSummaryContent.module.css';
import { getGYUserCountry, isITUser, isManagementUser } from "../../../../util/roles.js";
import { useUser } from "../../../../util/user.js";

const [ formatNumber ] = useFormatter();

export default function TargetViewSummaryContent(props) {
	const user = useUser();
	const { allSites_Select } = useSite();
	const allSites = useMemo(() => allSites_Select?.length ? allSites_Select.map(option => option.options).flat().map(option => option.value).filter(site => site.table_name) : [], [allSites_Select]);
	const [ data, setData ] = useState(null);
	const countryOptions = useMemo(() => ([
		{ label: "All", value: '_ALL' },
		{ label: "Thailand", value: 'TH' },
		{ label: "Vietnam", value: 'VN' },
		{ label: "Cambodia", value: 'KH' },
	]), []);
	const defaultSelectedCountry = useMemo(() => (
		((isITUser(user.roles) || isManagementUser(user.roles)) && '_ALL') ||
		getGYUserCountry(user.roles) ||
		'TH'
	), [user.roles]);
	const [selectedCountry, setSelectedCountry] = useState(countryOptions.find(opt => opt.value === defaultSelectedCountry));
	const productionSelectOptions = useMemo(() => ([
		{ label: "BP", value: 0 },
		{ label: "Asbuilt", value: 1 },
	]), []);
	const [selectedProduction, setSelectedProduction] = useState(productionSelectOptions[0]);
	const [ calculatedTargetData, setCalculatedTargetData ] = useState(null);
	const [fileName, setFileName] = useState("");	
	const [isLoading, setIsLoading] = useState(false);
	const firstLoad = useRef(false);

	/**
	 * Fetch target data
	 */
	const fetchTargetData = useCallback(async () => {
		const filterStr = `filter?column=id&value=ALL`;
		// API request to get target data
		let param, unused, egrid;
		try {
			param  = (await GYAxios.get(`/target/parameters/${filterStr}`)).data;
			unused = (await GYAxios.get(`/target/unused/${filterStr}`)).data;
			egrid  = (await GYAxios.get(`/target/egrid/${filterStr}`)).data;
		} catch(error) {
			console.error('Error while fetching target data: ', error);
			return null;
		}
		if(!param?.length || !unused?.length || !egrid?.length) {
			console.error('param or unused or egrid is empty!', { param, unused, egrid });
			return null;
		}
		return { param, unused, egrid };
	}, []);


	/**
	 * Calculate Target Data
	 * @param {obj} site
	 */
	const getTargetDataFromSite = useCallback((site, fetchedTargetData) => {
		// const contractDuration = site.contract_duration;
		const capacity = site.power;
		const cod = site.cod;
		const contractDurationYear = site.contract_duration;

		// site target data
		let { param, unused, egrid } = fetchedTargetData;
		param  = param.find(row =>  row.id === site.site_id);
		unused = unused.find(row => row.id === site.site_id);
		egrid  = egrid.find(row =>  row.id === site.site_id);

		// clone object
		param = JSON.parse(JSON.stringify(param));
		unused = JSON.parse(JSON.stringify(unused));
		egrid = JSON.parse(JSON.stringify(egrid));

		if(!param || !unused || !egrid) {
			return null;
		}

		// Calculate target production
		const summedCorrection = param.TA_correction + param.GA_correction + param.other_corrections;
		const yearlyDegradation = param.degradation;
		const targetProd = calculateTargetProd(
			egrid,
			capacity,
			summedCorrection,
			unused,
			yearlyDegradation,
			cod,
			contractDurationYear
		);

		return { BP: targetProd[0], Asbuilt: targetProd[1] };
	}, []);

	const buildTable = useCallback(async () => {
		setIsLoading(true);
		if(!allSites?.length) { return }
		const calculatedTargetData = {
			BP: { rows: [], columns: [] },
			Asbuilt: { rows: [], columns: [] }
		};
		const fetchedTargetData = await fetchTargetData();
		for(let site of allSites) {
			for(let type in calculatedTargetData) {
				const siteTargetData = getTargetDataFromSite(site, fetchedTargetData);
				if(!siteTargetData) {
					console.log('site not found in target data: ', site.table_name, 'type: ', type);
				}
				const target = siteTargetData?.[type];
				const { yearMonthColumns, staticColumns } = buildColumns(target);
				const row = buildRow(site, target, staticColumns);
				calculatedTargetData[type].rows.push(row);
				calculatedTargetData[type].columns = [...calculatedTargetData[type].columns, ...yearMonthColumns];
				calculatedTargetData[type].columns.sort((a, b) => a.field.localeCompare(b.field)); // sort columns by field name ASC);
				calculatedTargetData[type].columns = [...staticColumns, ...calculatedTargetData[type].columns]; // add static columns to the beginning
				calculatedTargetData[type].columns = calculatedTargetData[type].columns.filter((col, index) => calculatedTargetData[type].columns.findIndex(c => c.field === col.field) === index); // remove duplicate columns
			}
		}
		setCalculatedTargetData(calculatedTargetData);
		setIsLoading(false);
	}, [allSites, fetchTargetData, getTargetDataFromSite]);

	useEffect(() => {
		if(!allSites?.length) { return }
		if(firstLoad.current) { return }
		firstLoad.current = true;
		buildTable();
	}, [buildTable, allSites]);

	/**
	 * Build table columns
	 * @param {string} YYYY
	 * @param {number} years
	 * @returns table columns
	 */
	const buildColumns = (targetData) => {
		const width = 150;
		const staticColumns = [
			{ title: "SalesForce ID", field: "SF_project_id", width },
			{ title: "Country", field: "country", width: 80 },
			{ title: "Client Name", field: "company", width },
			{ title: "Site Name", field: "site_name", width },
		];
		const yearMonthColumns = (targetData || [])
			.flat()
			.filter(monthObj => typeof(monthObj.monthlyProd) === 'number')
			.sort((a, b) => a.year - b.year || parseInt(a.monthNo) - parseInt(b.monthNo)).map(monthObj => ({
				title: `${monthObj.year} ${toTitleCase(monthObj.month)}`,
				field: `${monthObj.year}_${monthObj.monthNo.toString().padStart(2, '0')}`,
				width
			}));
		return { yearMonthColumns, staticColumns };
	};

	/**
	 * Build table rows
	 * @param {string} YYYY
	 * @param {obj} data
	 * @returns table rows
	 */
	const buildRow = (site, target, staticColumns) => {
		const row = (target || [])
			.flat()
			.map(monthObj => ([
				`${monthObj.year}_${monthObj.monthNo.toString().padStart(2, '0')}`,
				(typeof(monthObj.monthlyProd) === 'number' && !isNaN(monthObj.monthlyProd)) ? monthObj.monthlyProd.toLocaleString() : '-'
			]));
		const staticValues = staticColumns.map(col => ([ col.field, site[col.field] ]));
		const rowObj = Object.fromEntries([ ...staticValues, ...row ]);
		return rowObj;
	};

	const rowsCountryFilter = useMemo(() => {
		let rows = calculatedTargetData?.[selectedProduction.label]?.rows || [];
		if(selectedCountry.value === '_ALL') { return rows }
		rows = rows.filter(row => row.country === selectedCountry.value);
		return rows;
	}, [calculatedTargetData, selectedProduction, selectedCountry.value]);

	return (
		<div>
			<div className={styles.inputControlContainer}>
				<div className={styles.inputControl} style={{ marginLeft: 'auto' }}>
					<span>Country:</span>
					<DefaultSelect
						options={countryOptions}
						value={selectedCountry}
						onChange={(opt) => setSelectedCountry(opt)}
						customStyle={{ width: "200px" }}
						placeholder="Select Country"
					/>
				</div>
				
				<div className={styles.inputControl}>
					<span>Type of Expected Production:</span>
					<DefaultSelect
						options={productionSelectOptions}
						value={selectedProduction}
						onChange={(opt) => setSelectedProduction(opt)}
						customStyle={{ width: "200px" }}
					/>
				</div>
			</div>
			<Row>
				<Col md="12">
					<MaterialTable
						columns={calculatedTargetData?.[selectedProduction.label]?.columns || []}
						data={rowsCountryFilter}
						isLoading={isLoading}
						options={{
							showTitle: false,
							exportButton: true,
							exportAllData: true,
							sorting: false,
							pageSize: 12,
							pageSizeOptions: [12],
							headerStyle: {
								fontSize: "1rem",
								textAlign: "center",
							},
							cellStyle: {
								textAlign: "center",
							},
							fixedColumns: {
								left: 4,
							},
							exportFileName: fileName,
						}}
					/>
				</Col>
			</Row>
		</div>
	);
}